import { Feature } from '@tencent/workbench';
import {
    handleInputBlurInitAndReset,
    IInputParams,
} from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { BaseFillFeature } from '@framework/workbench/drawer/ui/fill/base/base-fill-feature';
import { AllSpinOptions, FillType, TileOffsetSpinOptions } from '@framework/workbench/drawer/ui/fill/constants';
import {
    IFillRect,
    IImageFill,
    IImageTile,
    ImageTileFlipType,
} from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import i18n from '@i18next/i18n';
import { CUSTOM } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import { EEffectAlgn } from '@src/slide/domain/ui-model/element-modeles/property/effect/effect.interface';
import { getImageAlign, getMirrorType, ImageAlign, MirrorImageType } from '@editor/src/js/constants';
import { toJS, transaction } from 'mobx';
import { copy } from '@src/slide/infrastructure/module/independent/object-operation';
import {
    ImageReplaceAction,
} from '@src/slide/application/service/copy-paste/core/cp-image.new/image-edit-manager/core/image-edit-manager.interface';
import { opacityToAlpha } from '@framework/workbench/drawer/utils';
import type { NumberInputFrom } from '@tencent/wb-ext-form';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import ImageQuickLookManager
    from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-quick-look-manager';
import { tdwDrawerReport } from '@framework/workbench/drawer/report';
import { IImageFillFeature } from '@tencent/drawing-drawer-components';

const FILL_BG_TYPE_ALL = i18n.t('充满');
const FILL_BG_TYPE_TILE = i18n.t('平铺');
const DEFAULT_BG_IMG = CUSTOM.IMAGE.DEFAULT_FILL_IMAGE;
const DEFAULT_IMAGE_TILE = { tx: 0, ty: 0, sx: 100, sy: 100, flip: ImageTileFlipType.NONE, algn: EEffectAlgn.TL };
const DEFAULT_FILL_RECT = { l: 0, r: 0, b: 0, t: 0 };


interface IImageValue {
    rotateWithShape?: 0 | 1;
    imageAlpha: number;
    curImgFillType: string;
    curMirrorImageType: string;
    curImageAlign: string;
    tile: IImageTile;
    fillRect: IFillRect
}

@Feature()
export class ImageFillFeature extends BaseFillFeature implements IImageFillFeature {
    public value: IImageValue;
    public image;
    private imgfillTypes = [
        FILL_BG_TYPE_ALL, FILL_BG_TYPE_TILE,
    ];
    private mirrorImageTypes = MirrorImageType;
    private imageAlign = ImageAlign;
    private oldImageStretch;
    private isImageUpFromFile: boolean;

    public constructor() {
        super();
        this.value = {
            rotateWithShape: 1,
            imageAlpha: 1,
            curImgFillType: this.imgfillTypes[0],
            curMirrorImageType: this.mirrorImageTypes[0].id,
            curImageAlign: this.imageAlign[0].id,
            tile: DEFAULT_IMAGE_TILE,
            fillRect: DEFAULT_FILL_RECT,
        };
        this.addInitPropsCallback(this.getInputParams().key);
    }

    public getInputParams(): IInputParams {
        return {
            key: INPUT_PROPS_CALLBACK_KEY.IMAGE_FILL,
        };
    }

    public initPropsCallback = () => {
        if (this.fillType !== FillType.IMAGE_FILL) {
            return;
        }
        const fillInfo = this.fillInfo as IImageFill;
        this.value.imageAlpha = fillInfo?.alpha ?? 1;
        if (fillInfo?.tile) {
            this.value.tile = { ...DEFAULT_IMAGE_TILE, ...fillInfo.tile };
        }
        if (fillInfo?.stretch?.fillRect) {
            this.value.fillRect = { ...DEFAULT_FILL_RECT, ...fillInfo.stretch?.fillRect };
        }

        this.updateImage(this.fillColor.image);
    };

    public handleImageFillFromFile = async () => {
        const copyPasteImageService = await this.getAppServices()
            .editableServiceLayer?.copyPasteImageService?.promise();
        const { ImageEditManager } = copyPasteImageService ?? {};
        const imageManager = ImageEditManager?.getInstance();
        imageManager?.replaceImageFromFile(ImageReplaceAction.background);
        this.isImageUpFromFile = true;
    };

    public handleImageFillFromClipBoard = async () => {
        const copyPasteImageService = await this.getAppServices()
            .editableServiceLayer?.copyPasteImageService?.promise();
        const { ImageEditManager } = copyPasteImageService ?? {};
        const imageManager = ImageEditManager?.getInstance();
        imageManager?.replaceImageFromClipBoard(ImageReplaceAction.background);
        this.isImageUpFromFile = false;
    };

    public handleImageOpacityChange(value: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const { opacity } = this.getValidValue({ opacity: value }, from);
        const alpha = opacityToAlpha(opacity);
        const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        this.value.imageAlpha = alpha;
        transaction(() => {
            this.confirmImageChoice(String(alpha), onlyChangeUI);
        });
        this.tdwDrawerReportNumberInput(FormItemName.IMAGE_FILL_OPACITY, { ver6: opacity }, from, onlyChangeUI);
    }

    public handleImageFillTypeChange = (value) => {
        // 表明填充方式由充满切换为平铺，此时禁止裁剪
        if (this.value.curImgFillType === FILL_BG_TYPE_ALL && value === FILL_BG_TYPE_TILE) {
            if (this.getEditingStateManager().isCroping) {
                this.getEditingStateManager().updateElementCropState(false);
            }
        }
        this.value.curImgFillType = value;
        this.confirmImageChoice();
        const key = FormItemName.IMAGE_FILL_STYLE;
        const ver5 = value === FILL_BG_TYPE_ALL ? 1 : 2;
        tdwDrawerReport(key, { ver5 });
    };

    public handleImageAlignChange = (value: string) => {
        this.value.curImageAlign = value;
        this.confirmImageChoice();
    };

    public handleImageMirrorChange = (value) => {
        this.value.curMirrorImageType = value;
        this.confirmImageChoice();
    };

    @handleInputBlurInitAndReset()
    public handleTileChange(key: string, value: number, from: NumberInputFrom): void {
        if (key === 'tx') {
            const { tileTx } = this.getValidValue(
                { tileTx: value }, from,
                TileOffsetSpinOptions.max, TileOffsetSpinOptions.min,
            );
            this.value.tile[key] = tileTx * 2;
        } else if (key === 'ty') {
            const { tileTy } = this.getValidValue(
                { tileTy: value }, from,
                TileOffsetSpinOptions.max, TileOffsetSpinOptions.min,
            );
            this.value.tile[key] = tileTy * 2;
        } else if (key === 'sx') {
            const { tileSx } = this.getValidValue({ tileSx: value }, from);
            this.value.tile[key] = tileSx;
        } else if (key === 'sy') {
            const { tileSy } = this.getValidValue({ tileSy: value }, from);
            this.value.tile[key] = tileSy;
        }

        this.confirmImageChoice(undefined, this.isInputOnlyChangeUI());
    }

    @handleInputBlurInitAndReset()
    public handleFillRectChange(key: string, value: number, from: NumberInputFrom): void {
        if (key === 'l') {
            const { fillRectL } = this.getValidValue(
                { fillRectL: value }, from,
                AllSpinOptions.max, AllSpinOptions.min,
            );
            this.value.fillRect[key] = fillRectL / -100;
        } else if (key === 'r') {
            const { fillRectR } = this.getValidValue(
                { fillRectR: value }, from,
                AllSpinOptions.max, AllSpinOptions.min,
            );
            this.value.fillRect[key] = fillRectR / -100;
        } else if (key === 't') {
            const { fillRectT } = this.getValidValue(
                { fillRectT: value }, from,
                AllSpinOptions.max, AllSpinOptions.min,
            );
            this.value.fillRect[key] = fillRectT / -100;
        } else if (key === 'b') {
            const { fillRectB } = this.getValidValue(
                { fillRectB: value }, from,
                AllSpinOptions.max, AllSpinOptions.min,
            );
            this.value.fillRect[key] = fillRectB / -100;
        }

        this.confirmImageChoice(undefined, this.isInputOnlyChangeUI());
    }

    public handleRotateWithShapeChange = (value: boolean) => {
        this.value.rotateWithShape = value ? 1 : 0;
        this.confirmImageChoice();
    };

    public getImageProps = (imageAlpha?, onlyChangeUI?) => {
        let imageData;
        if (this.image) { // 之前设置过图片背景，
            if (imageAlpha && onlyChangeUI) {
                imageData = { alpha: imageAlpha };
            } else {
                imageData = copy(this.image, true);
                imageData.src = this.getImageSdkSrc(imageData.src, onlyChangeUI);
                imageData.alpha = String(this.value.imageAlpha);
                imageData.rotWithShape = this.value.rotateWithShape;
                const flip = this.value.curMirrorImageType;
                const algn = this.value.curImageAlign;
                if (this.value.curImgFillType === FILL_BG_TYPE_TILE) { // 设置了平铺
                    if (imageData.tile) {
                        imageData.tile = { ...this.value.tile };
                    } else {
                        imageData.tile = { ...DEFAULT_IMAGE_TILE }; // 默认值
                    }
                    imageData.tile.flip = flip;
                    imageData.tile.algn = algn;
                    delete imageData.stretch; // 平铺图片删除裁剪属性
                } else {
                    if (!imageData.stretch && this.oldImageStretch) {
                        imageData.stretch = this.oldImageStretch;
                    }
                    imageData.stretch = { ...imageData.stretch, fillRect: { ...this.value.fillRect }  };
                    delete imageData.tile; // 裁剪图片删除平铺属性
                }
            }
        } else { // 默认背景图片设置
            const tile = { ...DEFAULT_IMAGE_TILE };
            imageData = { src: DEFAULT_BG_IMG, rotWithShape: 1, tile, alpha: 1 };
        }
        return { image: imageData };
    };

    public confirmImageChoice = (imageAlpha?, onlyChangeUI?: boolean) => {
        const imageData = this.getImageProps(imageAlpha, onlyChangeUI);
        this.updateFillProps(imageData, onlyChangeUI);
    };

    private getImageSdkSrc = (src: string, onlyChangeUI?: boolean) => {
        if (!onlyChangeUI) {
            const model = this.getSelectModel()?.type === UIModelType.MULTIPLY
                ? (this.getSelectModel() as IMultiplyModel).selectedModels[0]
                : this.getSelectModel();
            // 此处ui存储的src和sdk的embed有差异，一个是http格式，一个是rel格式，会导致sdk在拦截未变更数据时出问题，导致数据重复提交
            // @ts-ignore
            const relId = model?.sdkData?.store?.data?.spPr?.blipFill?.blip?.embed;
            if (relId) return relId;
        }
        const quickLook = ImageQuickLookManager.getInstance();
        return quickLook.getResourceByUrl(src)?.id || src;
    };

    private updateImage = (imageFill?: IImageFill | null) => {
        const image = toJS(imageFill) as IImageFill;
        // 上报数据
        this.handleReportImageFill(image);
        this.image = copy(image, true);
        const rotateWithShape = typeof (image.rotWithShape) === 'undefined' ? 1 : image.rotWithShape;
        let curImgFillType;
        if (image.tile) {
            curImgFillType = FILL_BG_TYPE_TILE;
            this.value.curMirrorImageType = getMirrorType(image.tile.flip).id;
            this.value.curImageAlign = getImageAlign(image.tile.algn).id;
            this.value.tile = { ...this.value.tile, ...image.tile };
        } else {
            curImgFillType = FILL_BG_TYPE_ALL;
            if (image.stretch) {
                this.oldImageStretch = image.stretch;
            }
            this.value.fillRect = { ...this.value.fillRect, ...image.stretch?.fillRect };
        }
        this.value.curImgFillType = curImgFillType;
        this.value.rotateWithShape = rotateWithShape;
    };

    private handleReportImageFill = (image) => {
        if (!this.image || !image || this.image.src === image.src) return;
        if (this.isImageUpFromFile) {
            this.tdwDrawerReport(FormItemName.IMAGE_FILL_UPLOAD);
        } else {
            this.tdwDrawerReport(FormItemName.IMAGE_FILL_CLIPBOARD);
        }
    };
}
